import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { generateAutoBatches, getAutoBatchesPreflightData } from '../api/orders-other-actions';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import {
  BATCH_BY_ORDER_COUNT,
  BATCH_BY_BATCH_COUNT,
  MAX_ALLOWED_NUMBER_OF_ORDERS,
  MAX_ALLOWED_ORDERS_PER_BATCH,
  MAX_ALLOWED_NUMBER_OF_BATCHES,
} from './constants/auto-batching-constants';
import { OrderGridFilterPropType } from '../../../prop-types/order-grid-filter';
import { PRE_PROCESSING, PROCESSING, PROCESSED } from './constants/processing-state';
import { INVALID_NUMBER_OF_ORDERS, GENERAL_ERROR, ORDERS_INCLUDED_IN_OTHER_BATCH } from './constants/error-states';
import validationErrorMessages from './yup-validation-messages';
import { emptyArray } from 'ui-components/default-props/empty';

const validationMessageForBatchByOrderCount = {
  ...validationErrorMessages[BATCH_BY_ORDER_COUNT],
  values: {
    maxAllowedOrdersPerBatch: MAX_ALLOWED_ORDERS_PER_BATCH,
  },
};
const AutoBatchingContext = createContext({});

const AutoBatchingContextProvider = ({ children, gridFilters, totalRecords, selectedFulfillmentPlanIds = emptyArray }) => {
  const hasTooManyOrdersSelected = totalRecords > MAX_ALLOWED_NUMBER_OF_ORDERS;
  const [totalRecordsCount, setTotalRecordsCount] = useState(totalRecords);
  const [hasError, setHasError] = useAsyncSafeState(hasTooManyOrdersSelected);
  const [autoBatchingOptionType, setAutoBatchingOptionType] = useState(BATCH_BY_ORDER_COUNT);
  const [numberOfOrdersPerBatch, setNumberOfOrdersPerBatch] = useState(0);
  const [numberOfBatches, setNumberOfBatches] = useState(0);
  const [batchNotes, setBatchNotes] = useState();
  const [autoBatchResponse, setAutoBatchResponse] = useAsyncSafeState({});
  const [processState, setProcessingState] = useAsyncSafeState(hasTooManyOrdersSelected ? PRE_PROCESSING : PROCESSING);
  const [hasGeneralError, setHasGeneralError] = useAsyncSafeState(false);
  const [hasConfirmedForceMove, setHasConfirmedForceMove] = useState(false);
  const [fulfillmentPlanIdsInOtherBatches, setFulfillmentPlanIdsInOtherBatches] = useAsyncSafeState([]);
  const [hasExcludedBatchedFulfillmentPlanIds, setHasExcludedBatchedFulfillmentPlanIds] = useState(false);
  const [hasCheckedPreFlight, setHasCheckedPreFlight] = useAsyncSafeState(false);

  const onGetAutoBatchPreflight = useCallback(() => {
    const request = {
      filter: gridFilters,
      selectedFulfillmentPlanIds,
    };
    getAutoBatchesPreflightData(request).then((response) => {
      setHasCheckedPreFlight(true);
      const { data } = response;
      if (!Array.isArray(data) || data.length === 0) {
        setFulfillmentPlanIdsInOtherBatches([]);
        setProcessingState(PRE_PROCESSING);
        return;
      }
      setFulfillmentPlanIdsInOtherBatches(data);
      setHasError(true);
      setProcessingState(PRE_PROCESSING);
    });
  }, [
    gridFilters,
    selectedFulfillmentPlanIds,
    setFulfillmentPlanIdsInOtherBatches,
    setHasError,
    setHasCheckedPreFlight,
    setProcessingState,
  ]);

  useEffect(() => {
    if (hasCheckedPreFlight || hasError) {
      return;
    }
    onGetAutoBatchPreflight();
  }, [onGetAutoBatchPreflight, hasCheckedPreFlight, hasError]);

  const onHasExcludedFulfillmentPlanIds = useCallback(() => {
    setHasExcludedBatchedFulfillmentPlanIds(true);
    setTotalRecordsCount(totalRecordsCount - fulfillmentPlanIdsInOtherBatches.length);
    setHasError(false);
  }, [setHasExcludedBatchedFulfillmentPlanIds, setTotalRecordsCount, totalRecordsCount, fulfillmentPlanIdsInOtherBatches, setHasError]);

  const onHasConfirmedForceMove = useCallback(() => {
    setHasConfirmedForceMove(true);
    setHasError(false);
  }, [setHasConfirmedForceMove, setHasError]);

  const getAnticipatedCountFromTotalRecords = useCallback((value) => Math.ceil(totalRecordsCount / value), [totalRecordsCount]);
  const hasBatchWithInconsistentOrderCount = useCallback(() => {
    return totalRecordsCount % numberOfBatches > 0 || totalRecordsCount % numberOfOrdersPerBatch > 0;
  }, [totalRecordsCount, numberOfBatches, numberOfOrdersPerBatch]);

  const onHasGeneralError = useCallback(() => {
    setHasGeneralError(true);
    setHasError(true);
  }, [setHasGeneralError, setHasError]);

  const onGenerateBatches = useCallback(() => {
    const batchSize = Math.ceil(numberOfOrdersPerBatch > 0 ? numberOfOrdersPerBatch : totalRecords / numberOfBatches);
    const request = {
      batchSize,
      batchNotes,
      filter: gridFilters,
      selectedFulfillmentPlanIds,
      excludedFulfillmentPlanIds: hasExcludedBatchedFulfillmentPlanIds ? fulfillmentPlanIdsInOtherBatches : [],
      hasConfirmedForceMove,
    };

    setHasError(false);
    setProcessingState(PROCESSING);
    return generateAutoBatches(request)
      .then((response) => {
        const { succeeded, batchResults, totalBatchCount } = response.data;
        if (!succeeded || !batchResults || !batchResults.length) {
          setProcessingState(PRE_PROCESSING);
          onHasGeneralError();
          return;
        }

        const orderCountToTotalBatches = [...new Set(batchResults.map(({ count }) => count))].map((count) => {
          return {
            orderCount: count,
            totalBatchesWithCount: batchResults.filter((res) => res.count === count).length,
          };
        });
        setAutoBatchResponse({
          totalBatchCount,
          orderCountToTotalBatches,
        });
        setProcessingState(PROCESSED);
      })
      .catch(() => {
        setProcessingState(PRE_PROCESSING);
        onHasGeneralError();
      });
  }, [
    numberOfOrdersPerBatch,
    batchNotes,
    gridFilters,
    selectedFulfillmentPlanIds,
    setHasError,
    setProcessingState,
    setAutoBatchResponse,
    onHasGeneralError,
    hasConfirmedForceMove,
    hasExcludedBatchedFulfillmentPlanIds,
    fulfillmentPlanIdsInOtherBatches,
    totalRecords,
    numberOfBatches,
  ]);

  const schemaToPreventOrderCountOverageForTotal = useMemo(
    () =>
      Yup.object({
        [BATCH_BY_ORDER_COUNT]: Yup.number().lessThan(totalRecordsCount + 1, validationErrorMessages.autoBatchTotalOrdersExceeded),
      }),
    [totalRecordsCount],
  );

  const schemaToPreventOrderCountOverageForMaxAllowed = useMemo(
    () =>
      Yup.object({
        [BATCH_BY_ORDER_COUNT]: Yup.number().lessThan(MAX_ALLOWED_ORDERS_PER_BATCH + 1, validationMessageForBatchByOrderCount),
      }),
    [],
  );

  const schemaToPreventBatchCountOverageForTotal = useMemo(
    () =>
      Yup.object({
        [BATCH_BY_BATCH_COUNT]: Yup.number().lessThan(totalRecordsCount + 1, validationErrorMessages.autoBatchTotalOrdersExceeded),
      }),
    [totalRecordsCount],
  );

  const schemaToPreventBatchCountOverageForMaxAllowed = useMemo(
    () =>
      Yup.object({
        [BATCH_BY_BATCH_COUNT]: Yup.number().lessThan(MAX_ALLOWED_NUMBER_OF_BATCHES, validationErrorMessages[BATCH_BY_BATCH_COUNT]),
      }),
    [],
  );

  let yupSchema;
  if (autoBatchingOptionType === BATCH_BY_ORDER_COUNT) {
    yupSchema =
      totalRecordsCount < MAX_ALLOWED_ORDERS_PER_BATCH
        ? schemaToPreventOrderCountOverageForTotal
        : schemaToPreventOrderCountOverageForMaxAllowed;
  } else {
    yupSchema =
      totalRecordsCount < MAX_ALLOWED_NUMBER_OF_BATCHES
        ? schemaToPreventBatchCountOverageForTotal
        : schemaToPreventBatchCountOverageForMaxAllowed;
  }

  const errorState = useMemo(() => {
    if (totalRecordsCount > MAX_ALLOWED_NUMBER_OF_ORDERS) {
      return INVALID_NUMBER_OF_ORDERS;
    }
    if (fulfillmentPlanIdsInOtherBatches.length > 0 && !hasConfirmedForceMove && !hasExcludedBatchedFulfillmentPlanIds) {
      return ORDERS_INCLUDED_IN_OTHER_BATCH;
    }
    if (hasGeneralError) {
      return GENERAL_ERROR;
    }
  }, [hasGeneralError, totalRecordsCount, fulfillmentPlanIdsInOtherBatches, hasConfirmedForceMove, hasExcludedBatchedFulfillmentPlanIds]);

  const value = {
    hasError,
    numberOfOrdersPerBatch,
    setNumberOfOrdersPerBatch,
    numberOfBatches,
    setNumberOfBatches,
    batchNotes,
    setBatchNotes,
    autoBatchResponse,
    processState,
    onGenerateBatches,
    totalRecords: totalRecordsCount,
    errorState,
    hasConfirmedForceMove,
    onGetAutoBatchPreflight,
    onHasConfirmedForceMove,
    onHasExcludedFulfillmentPlanIds,
    totalAlreadyBatched: fulfillmentPlanIdsInOtherBatches.length,
    getAnticipatedCountFromTotalRecords,
    autoBatchingOptionType,
    setAutoBatchingOptionType,
    hasBatchWithInconsistentOrderCount,
    yupSchema,
  };
  return <AutoBatchingContext.Provider value={value}>{children}</AutoBatchingContext.Provider>;
};

AutoBatchingContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  gridFilters: OrderGridFilterPropType,
  totalRecords: PropTypes.number.isRequired,
  selectedFulfillmentPlanIds: PropTypes.arrayOf(PropTypes.string),
};

export { AutoBatchingContext, AutoBatchingContextProvider };
